import { envelopeData } from '@/lib/envelope'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Info, Trophy } from 'lucide-react'
import { leaderboardQueryOptions, DEFAULT_LEADERBOARD_LIMIT } from '@/lib/queries/leaderboard'
import { quickStatsQueryOptions } from '@/lib/queries/dashboard'
import {
  LeaderboardList,
  LeaderboardListSkeleton,
  type LeaderboardEntry,
} from '@/components/ranking/leaderboard-list'
import {
  LeaderboardMe,
  LeaderboardMeSkeleton,
  type StudentRanking,
} from '@/components/ranking/leaderboard-me'

export const Route = createFileRoute('/_app/ranking')({
  loader: async ({ context }) => {
    // As DUAS leituras no prefetch: a lista sem o quick-stats renderiza o topo
    // sem a linha "você", e o aluno fora do top 10 abriria a página sem
    // resposta nenhuma sobre si — que é o principal motivo de ele entrar aqui.
    await Promise.allSettled([
      context.queryClient.prefetchQuery(leaderboardQueryOptions()),
      context.queryClient.prefetchQuery(quickStatsQueryOptions()),
    ])
  },
  component: RankingPage,
  pendingComponent: RankingPending,
})

function RankingPending() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="space-y-2">
        <div className="h-7 w-40 animate-pulse rounded-md bg-accent" />
        <div className="h-4 w-72 animate-pulse rounded-md bg-accent" />
      </div>
      <LeaderboardMeSkeleton />
      <LeaderboardListSkeleton rows={DEFAULT_LEADERBOARD_LIMIT} />
    </div>
  )
}

function RankingPage() {
  const { data: leaderboardData, isPending: isLeaderboardPending } = useQuery(leaderboardQueryOptions())
  const { data: statsData, isPending: isStatsPending } = useQuery(quickStatsQueryOptions())

  const entries = (envelopeData(leaderboardData) ?? []) as LeaderboardEntry[]
  const stats = envelopeData(statsData) as StudentRanking | undefined

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="space-y-1">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <Trophy className="h-5 w-5 text-achievement" />
          Ranking
        </h1>
        <p className="text-sm text-muted-foreground">
          Os {DEFAULT_LEADERBOARD_LIMIT} alunos com mais XP. A posição considera todos os alunos, e o XP
          vem das aulas concluídas, dos cursos terminados e da sua sequência de dias.
        </p>
      </div>

      {isStatsPending ? <LeaderboardMeSkeleton /> : stats && <LeaderboardMe ranking={stats} />}

      {isLeaderboardPending ? (
        <LeaderboardListSkeleton rows={DEFAULT_LEADERBOARD_LIMIT} />
      ) : (
        <LeaderboardList entries={entries} myPosition={stats?.rankingPosition} />
      )}

      {/*
        A lista é pública dentro do workspace: os colegas leem nome e XP. Dizer
        isso na própria tela é o mínimo — o aluno nunca consentiu explicitamente
        em aparecer, e a api entrou com opt-out (D4) justamente por isso.
        ⚠️ O controle de sair/entrar ainda não existe: falta a rota de ESCRITA
        de `customers.leaderboard_opt_out` na api. Enquanto ela não chega, esta
        tela não promete um botão que não funcionaria.
      */}
      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Esta lista é visível para os outros alunos deste espaço: aparecem o nome e o XP. Quem não
          informou o nome no cadastro aparece apenas pela posição.
        </span>
      </p>
    </div>
  )
}
